import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, ChevronDown, Clock, Mail, MessageSquare, Paperclip, Send, ShieldCheck, X } from 'lucide-react';
import polyConfig from '../../poly.json';
import { normalizeFeedbackError } from '../lib/feedbackMessages';

const INITIAL_FORM = {
  name: '',
  email: '',
  issueType: '',
  customIssue: '',
  message: '',
};

const SUPPORT_EMAIL = 'support.linkit@gmail.com';
const SUPPORT_COMPOSE_URL = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(SUPPORT_EMAIL)}&su=${encodeURIComponent('Linkit support')}`;
const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
const MAX_ATTACHMENT_TOTAL_BYTES = 5 * 1024 * 1024;
const ACCEPTED_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'text/plain',
  'text/csv',
  'application/json',
];
const ACCEPTED_ATTACHMENT_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gif',
  '.pdf',
  '.mp4',
  '.webm',
  '.mov',
  '.txt',
  '.log',
  '.csv',
  '.json',
];
const DANGEROUS_ATTACHMENT_EXTENSIONS = new Set([
  'app', 'bat', 'cmd', 'com', 'cpl', 'dll', 'dmg', 'exe', 'hta', 'html', 'htm',
  'iso', 'jar', 'js', 'jse', 'lnk', 'msi', 'ps1', 'scr', 'sh', 'svg', 'vb',
  'vbe', 'vbs', 'wsf', 'xhtml', 'zip', 'rar', '7z', 'gz', 'tar',
]);

const SUPPORT_ISSUES = [
  { value: 'account_access', label: 'Account access' },
  { value: 'wallet_pass', label: 'Wallet pass' },
  { value: 'public_profile', label: 'Public profile' },
  { value: 'vault_security', label: 'Vault or security' },
  { value: 'billing_subscription', label: 'Billing or subscription' },
  { value: 'bug_report', label: 'Bug report' },
  { value: 'abuse_safety', label: 'Abuse or safety report' },
  { value: 'other', label: 'Something else' },
];

const getExtension = (filename = '') => {
  const match = String(filename || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve(result.includes(',') ? result.split(',').pop() : result);
  };
  reader.onerror = () => reject(new Error('Could not read attachment.'));
  reader.readAsDataURL(file);
});

const formatFileSize = (bytes = 0) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
};

/* ── Custom Animated Select ── */
const CustomSelect = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selectedOption = options.find((o) => o.value === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="support-input w-full text-left flex justify-between items-center cursor-pointer"
      >
        <span className={selectedOption ? 'text-[var(--support-ink)]' : 'text-[var(--support-ink)]/50'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`text-[var(--support-ink)] opacity-40 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="support-dropdown-menu absolute z-50 w-full mt-2 py-1.5"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`support-dropdown-item w-full text-left px-5 py-3 text-sm font-bold transition-colors ${
                  value === option.value
                    ? 'support-dropdown-item-active'
                    : ''
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SupportPage = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [attachments, setAttachments] = useState([]);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'issueType' && value !== 'other' ? { customIssue: '' } : {}),
    }));
    if (error) setError('');
  };

  const handleAttachmentChange = (event) => {
    const incomingFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (!incomingFiles.length) return;

    const nextFiles = [...attachments];
    const rejected = [];

    for (const file of incomingFiles) {
      const extension = getExtension(file.name);
      const mimeType = String(file.type || '').toLowerCase();
      const totalBytes = nextFiles.reduce((sum, item) => sum + item.size, 0) + file.size;

      if (nextFiles.length >= MAX_ATTACHMENTS) {
        rejected.push(`Attach up to ${MAX_ATTACHMENTS} files.`);
        break;
      }
      if (!ACCEPTED_ATTACHMENT_TYPES.includes(mimeType) || DANGEROUS_ATTACHMENT_EXTENSIONS.has(extension)) {
        rejected.push(`${file.name} is not a supported evidence file.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        rejected.push(`${file.name} is over ${formatFileSize(MAX_ATTACHMENT_BYTES)}.`);
        continue;
      }
      if (totalBytes > MAX_ATTACHMENT_TOTAL_BYTES) {
        rejected.push(`Attachments can be ${formatFileSize(MAX_ATTACHMENT_TOTAL_BYTES)} total.`);
        continue;
      }

      nextFiles.push(file);
    }

    setAttachments(nextFiles);
    if (rejected.length) setError(rejected[0]);
    else if (error) setError('');
  };

  const removeAttachment = (indexToRemove) => {
    setAttachments((current) => current.filter((_, index) => index !== indexToRemove));
    if (error) setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const issueType = form.issueType.trim();
    const customIssue = form.customIssue.trim();
    const message = form.message.trim();
    const selectedIssue = SUPPORT_ISSUES.find((issue) => issue.value === issueType);
    if (!name || !email || !issueType || (issueType === 'other' && !customIssue) || !message) return;

    setSubmitting(true);
    setError('');

    try {
      const encodedAttachments = await Promise.all(attachments.map(async (file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
        data: await fileToBase64(file),
      })));
      const response = await fetch('/api/support-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [polyConfig.HEADER_NAME]: polyConfig.HEADER_VALUE,
        },
        body: JSON.stringify({
          identifier: email,
          category: selectedIssue?.label || issueType,
          source: 'support_page',
          message: {
            name,
            text: message,
            customIssue,
            attachments: encodedAttachments,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const normalized = normalizeFeedbackError(payload.details || payload.error, {
          title: 'Support request was not sent',
          description: 'Your message remains on this screen. Try again or email support directly.',
        });
        setError(`${normalized.description} Reference: ${normalized.reference}`);
        return;
      }

      setSent(true);
      setForm(INITIAL_FORM);
      setAttachments([]);
    } catch (err) {
      setError('Network error. Your message is still here — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="linkit-owned-page support-premium-page min-h-screen px-4 py-4 text-[#17312b] sm:px-6 sm:py-6 lg:px-10">
      <header className="support-premium-header mx-auto flex max-w-6xl items-center justify-between">
        <Link to="/" className="support-premium-brand inline-flex items-center gap-3 text-sm font-black">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl text-lg">L</span>
          <span className="linkit-product-wordmark text-xl">linkit</span>
        </Link>
        <Link to="/" className="support-premium-home inline-flex items-center gap-2 rounded-full px-4 py-2 text-[10px] font-black uppercase tracking-widest">
          <ArrowLeft size={14} />
          Home
        </Link>
      </header>

      <section className="support-premium-layout mx-auto grid max-w-6xl gap-8 lg:grid-cols-12 lg:gap-12 py-8 lg:items-start lg:py-12">
        <aside className="support-premium-intro lg:col-span-5 min-w-0">
          <div className="space-y-5">
            <div className="support-premium-kicker inline-flex items-center gap-2 rounded-full px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em]">
              <MessageSquare size={14} />
              Linkit support
            </div>
            <h1 className="max-w-xl text-5xl leading-[0.92] tracking-tight sm:text-6xl">
              Get the right help without losing the details.
            </h1>
            <p className="max-w-lg text-base font-semibold leading-7">
              Send one clear request for account access, profiles, wallet passes, billing, safety, or security. Your message and attachments stay on this screen until the request is accepted.
            </p>
          </div>

          <div className="support-premium-guidance mt-8 grid gap-3">
            {[
              { icon: <Clock size={17} />, title: 'Reply destination', desc: 'We reply to the email entered in your request.' },
              { icon: <ShieldCheck size={17} />, title: 'Useful evidence only', desc: 'Add usernames, domains, screenshots, or logs only when they explain the issue.' },
              { icon: <Mail size={17} />, title: 'Short question', desc: 'Use direct email when a full support request is unnecessary.' },
            ].map((item) => (
              <div key={item.title} className="support-premium-guidance-item rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="support-premium-guidance-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
                    {item.icon}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-sm font-black">{item.title}</h2>
                    <p className="mt-1 text-sm font-semibold leading-6">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <a
            href={SUPPORT_COMPOSE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="support-premium-email mt-6 inline-flex max-w-full items-center gap-3 rounded-2xl px-5 py-4 text-sm font-black transition-transform hover:-translate-y-0.5"
          >
            <Mail size={18} />
            <span className="min-w-0 break-all">{SUPPORT_EMAIL}</span>
          </a>
        </aside>

        {/* ── Form Card ── */}
        <section className="support-premium-form-card lg:col-span-7 min-w-0 box-border">
          <div className="support-form-inner">
            {/* Header */}
            <div className="mb-10 flex flex-wrap items-center justify-between gap-4 border-b border-[var(--support-line)] pb-7">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[var(--support-accent)]">Request details</p>
                <h2 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl">How can we help?</h2>
              </div>
              <span className="support-premium-reply-badge rounded-full px-4 py-2 text-[10px] font-black uppercase tracking-widest">
                Reply by email
              </span>
            </div>

            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="flex min-h-[360px] flex-col items-center justify-center gap-5 text-center"
              >
                <CheckCircle2 size={44} className="text-[var(--support-accent)]" />
                <div>
                  <h2 className="text-3xl font-black tracking-tight">Message sent</h2>
                  <p className="mt-2 text-sm font-bold text-[var(--support-muted)]">We will reply by email.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="support-btn-secondary rounded-2xl px-6 py-3 text-xs font-black uppercase tracking-widest transition-transform hover:-translate-y-0.5"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-7">
                {/* Name + Email */}
                <div className="grid gap-6 sm:grid-cols-2">
                  <label className="support-field">
                    <span className="support-label">Name</span>
                    <input
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={updateField}
                      className="support-input"
                      placeholder="Your name"
                    />
                  </label>
                  <label className="support-field">
                    <span className="support-label">Email</span>
                    <input
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={updateField}
                      className="support-input"
                      placeholder="you@example.com"
                    />
                  </label>
                </div>

                {/* Issue Type — Custom Select */}
                <label className="support-field">
                  <span className="support-label">Issue type</span>
                  <CustomSelect
                    value={form.issueType}
                    onChange={(val) => setForm((prev) => ({ ...prev, issueType: val }))}
                    options={SUPPORT_ISSUES}
                    placeholder="Choose the closest issue"
                  />
                </label>

                {/* Custom Issue (conditional) */}
                {form.issueType === 'other' && (
                  <label className="support-field">
                    <span className="support-label">Issue not listed</span>
                    <input
                      name="customIssue"
                      type="text"
                      required
                      value={form.customIssue}
                      onChange={updateField}
                      placeholder="Describe your issue"
                      className="support-input"
                    />
                  </label>
                )}

                {/* Message */}
                <label className="support-field">
                  <span className="support-label">Message</span>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={updateField}
                    className="support-input resize-none"
                    placeholder="Tell us what happened…"
                  />
                </label>

                {/* Evidence Files */}
                <div className="space-y-4">
                  <div className="support-evidence-zone flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <span className="support-label">Evidence files</span>
                      <p className="mt-1 text-xs font-bold leading-5 text-[var(--support-muted)]">
                        Images, PDF, short video, TXT, CSV, JSON, or logs. Max {MAX_ATTACHMENTS} files, {formatFileSize(MAX_ATTACHMENT_BYTES)} each.
                      </p>
                    </div>
                    <label className="support-btn-attach inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-[10px] font-black uppercase tracking-widest transition-transform hover:-translate-y-0.5">
                      <Paperclip size={14} />
                      Attach
                      <input
                        type="file"
                        multiple
                        accept={ACCEPTED_ATTACHMENT_EXTENSIONS.join(',')}
                        onChange={handleAttachmentChange}
                        className="sr-only"
                      />
                    </label>
                  </div>
                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((file, index) => (
                        <div key={`${file.name}-${file.size}-${index}`} className="support-file-row flex items-center justify-between gap-3 rounded-2xl px-5 py-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-black">{file.name}</p>
                            <p className="mt-0.5 text-[10px] font-black uppercase tracking-widest opacity-50">{formatFileSize(file.size)} · checked before upload</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="support-file-remove flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition"
                            aria-label={`Remove ${file.name}`}
                          >
                            <X size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Error */}
                {error && (
                  <p className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-xs font-black text-rose-900">
                    {error}
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="support-btn-submit inline-flex w-full items-center justify-center gap-3 rounded-2xl px-6 py-5 text-sm font-black uppercase tracking-widest transition-all active:translate-y-[1px] disabled:opacity-55"
                >
                  <Send size={16} />
                  {submitting ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </section>
      </section>
    </main>
  );
};

export default SupportPage;
